import { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { CollectionsViewPageHeaderShareButton } from "@/pages/collections-view/components/collections-view-page-header-share-button";
import styled from "@emotion/styled";
import { CollectionsFacetFilter } from "@/components/filters/collections-facet-filter";
import { ContactsFacetFilter } from "@/components/filters/contacts-facet-filter";
import { NotesListFilter } from "@/modules/filters/notes/NotesListFilter";
import { DateFacetFilter } from "@/components/filters/date-facet-filter";
import { MediaKindsFacetFilter } from "@/components/filters/media-kinds-facet-filter";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import {
  MDSCollectionIcon,
  MDSIconButton,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSEditableTitle,
  MDSPanelHeader,
  MDSPanelHeaderSlotName,
  MDSTextVariant,
  MDSIconButtonSize,
  MDSPanelHeaderButtonGroup,
  MDSIconKind,
  MDSActionMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSFadeHorizontalScroll,
} from "@/mds";
import { MDSPanelWidths } from "@/mds/foundations";
import { SortByDropdownButton } from "@/components/filters/sort-by-dropdown-button";
import { ClearFiltersModal, useClearFiltersModal } from "@/components/filters/clear-filters-modal";
import { SortByKind } from "@/modules/lenses/types";

export const CollectionsViewPageHeader = observer(() => {
  const page = useTabPageStore("collectionsViewPage");
  const collection = page.collectionObservable;
  const { filters } = page;

  const collectionTitleInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (collection && !collection.label) collectionTitleInputRef.current?.focus();
  }, [collection]);

  useEffect(() => {
    // Make sure to save the collection when the component unmounts because blur is not guaranteed
    // to be called whe navigating away from the page.
    return () => {
      if (collection && !collection.isDeleted) collection.save();
    };
  }, [collection]);

  const {
    filterButtonRef,
    showFilters,
    showFiltersModal,
    setShowFiltersModal,
    handleToggleFilters,
    handleClearFilters,
  } = useClearFiltersModal({
    clearAllFilters: page.filters.clearAllFilters,
    activeFiltersCount: page.filters.activeFiltersCount,
  });

  if (!collection) return null;
  return (
    <>
      <Wrapper>
        <MDSPanelHeader>
          <StyledHeaderHeading slot={MDSPanelHeaderSlotName.Heading}>
            <MDSCollectionIcon boxSize={40} size={20} collectionId={collection.id} />
            <MDSEditableTitle
              variant={MDSTextVariant.HeaderL}
              placeholder={"Untitled collection"}
              value={collection.title}
              onFocus={() => collection.lock()}
              onChange={e => collection.setTitle(e.currentTarget.value)}
              onBlur={async () => await collection.save()}
              autoFocus={!collection?.title}
              readOnly={!collection.canWrite}
            />
          </StyledHeaderHeading>
          <StyledHeaderSubsection slot={MDSPanelHeaderSlotName.Subsection}>
            <MDSEditableTitle
              variant={MDSTextVariant.ParagraphS}
              value={collection.description}
              placeholder={"Add a description"}
              onFocus={() => collection.lock()}
              onChange={e => collection.setDescription(e.currentTarget.value)}
              onBlur={() => collection.save()}
              autoFocus={collection?.label === ""}
              readOnly={!collection.canWrite}
            />
          </StyledHeaderSubsection>
          <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
            <MDSTooltip
              config={{
                label: "Create note in collection",
                placement: MDSTooltipPlacement.Top,
              }}
            >
              <MDSIconButton
                iconKind={MDSIconKind.Plus}
                onClick={() => page.handleCreateNewNote()}
                size={MDSIconButtonSize.Medium}
              />
            </MDSTooltip>

            <CollectionsViewPageHeaderShareButton collection={collection} />

            <MDSTooltip
              config={{
                label: collection.isFavorited ? "Unpin from sidebar" : "Pin to sidebar",
                placement: MDSTooltipPlacement.Top,
              }}
            >
              <MDSIconButton
                iconKind={
                  collection.isFavorited ? MDSIconKind.ThumbtackSolid : MDSIconKind.Thumbtack
                }
                onClick={() => collection.toggleFavorite()}
                size={MDSIconButtonSize.Medium}
              />
            </MDSTooltip>
            <MDSTooltip config={{ label: "Show filters", placement: MDSTooltipPlacement.Top }}>
              <MDSIconButton
                ref={filterButtonRef}
                iconKind={MDSIconKind.Filter}
                onClick={handleToggleFilters}
                isToggled={showFilters}
                size={MDSIconButtonSize.Medium}
              />
            </MDSTooltip>
            <MDSTooltip config={{ label: "More", placement: MDSTooltipPlacement.Top }}>
              <MDSActionMenuDropdown
                contentList={page.headerDropdownButtonContentList}
                placement="bottom right"
              >
                <MDSIconButton slot={MDSMenuDropdownSlotName.Trigger} iconKind={MDSIconKind.More} />
              </MDSActionMenuDropdown>
            </MDSTooltip>
          </MDSPanelHeaderButtonGroup>
        </MDSPanelHeader>
        {showFilters && (
          <WrapperBottom>
            <FiltersContainer>
              <SortByDropdownButton
                sortOptions={filters.sortOptions}
                defaultSortOption={SortByKind.LastModified}
                selectedSortOption={filters.sortBy}
                onSelectSortOption={filters.setSortBy}
              />
              {renderSortedFilters(page.filters)}
            </FiltersContainer>
          </WrapperBottom>
        )}
      </Wrapper>
      <ClearFiltersModal
        triggerRef={filterButtonRef}
        isOpen={showFiltersModal}
        count={page.filters.activeFiltersCount}
        onCancel={() => setShowFiltersModal(false)}
        onConfirm={handleClearFilters}
      />
    </>
  );
});

const renderSortedFilters = (pageFilters: NotesListFilter) => {
  const filters = [
    {
      isActive: !!pageFilters.params.filter?.date,
      defaultOrder: 2,
      component: (
        <DateFacetFilter
          key="date"
          items={pageFilters.dateFilterItems}
          selectedRange={pageFilters.dateSelectedRange}
          onClear={() => pageFilters.clearDateFilter()}
        />
      ),
    },
    {
      isActive: !!pageFilters.mediaKindsFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 1,
      component: (
        <MediaKindsFacetFilter
          key="media-kinds"
          items={pageFilters.mediaKindsFacetFilterItems}
          onSelectItem={pageFilters.toggleMediaKindItem}
          onSelectClear={pageFilters.clearMediaKinds}
          selectedItems={pageFilters.selectedMediaKinds}
        />
      ),
    },
    {
      isActive: pageFilters.addedBySearchItems.some(item => item.isSelected),
      defaultOrder: 3,
      component: (
        <ContactsFacetFilter
          key="addedBy"
          items={pageFilters.addedBySearchItems}
          searchText={pageFilters.addedBySearchQuery}
          onSearchTextChange={pageFilters.setAddedBySearchQuery}
          onSearchTextClear={pageFilters.clearAddedBySearchQuery}
          onSelectItem={pageFilters.toggleAddedByItem}
          onSelectClear={pageFilters.clearAddedBy}
          buttonPrefix="Added by"
        />
      ),
    },
    {
      isActive: pageFilters.modifiedBySearchItems.some(item => item.isSelected),
      defaultOrder: 4,
      component: (
        <ContactsFacetFilter
          key="modifiedBy"
          items={pageFilters.modifiedBySearchItems}
          searchText={pageFilters.modifiedBySearchQuery}
          onSearchTextChange={pageFilters.setModifiedBySearchQuery}
          onSearchTextClear={pageFilters.clearModifiedBySearchQuery}
          onSelectItem={pageFilters.toggleModifiedByItem}
          onSelectClear={pageFilters.clearModifiedBy}
          buttonPrefix="Edited by"
        />
      ),
    },
    {
      isActive: pageFilters.createdBySearchItems.some(item => item.isSelected),
      defaultOrder: 5,
      component: (
        <ContactsFacetFilter
          key="createdBy"
          items={pageFilters.createdBySearchItems}
          searchText={pageFilters.createdBySearchQuery}
          onSearchTextChange={pageFilters.setCreatedBySearchQuery}
          onSearchTextClear={pageFilters.clearCreatedBySearchQuery}
          onSelectItem={pageFilters.toggleCreatedByItem}
          onSelectClear={pageFilters.clearCreatedBy}
          buttonPrefix="Created by"
        />
      ),
    },
    {
      isActive:
        pageFilters.selectedCollectionTitles.length > 0 ||
        pageFilters.collectionNoCollectionIsSelected,
      defaultOrder: 0,
      component: (
        <CollectionsFacetFilter
          key="collections"
          searchText={pageFilters.collectionsSearchQuery}
          items={pageFilters.collectionsFilterItems}
          onSearchTextChange={pageFilters.setCollectionSearchQuery}
          onSearchTextClear={pageFilters.clearCollectionSearchQuery}
          onSelectItem={pageFilters.toggleCollectionItem}
          onSelectClear={pageFilters.clearCollections}
          buttonPrefix={pageFilters.collectionNoCollectionIsSelected ? "" : "In"}
          buttonLabel="Collections"
          selectedModeKey={pageFilters.selectedCollectionModeKey}
          onSelectMode={pageFilters.onCollectionModeSelectionChange}
          noCollectionIsSelected={pageFilters.collectionNoCollectionIsSelected}
          onNoCollectionIsSelected={pageFilters.setCollectionNoCollectionIsSelected}
          selectedItems={pageFilters.selectedCollectionTitles}
        />
      ),
    },
  ];

  return filters
    .sort((a, b) => {
      const activeSort = Number(b.isActive) - Number(a.isActive);
      if (activeSort !== 0) {
        return activeSort;
      }

      return a.defaultOrder - b.defaultOrder;
    })
    .map(filter => filter.component);
};

const Wrapper = styled.div({});

const WrapperBottom = styled.div(({ theme }) => ({
  padding: theme.spacing["4"],
}));

const StyledHeaderHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledHeaderSubsection = styled.div`
  padding-top: ${({ theme }) => theme.spacing["4"]};
  padding-bottom: ${({ theme }) => theme.spacing["3"]};

  @container mds-panel (max-width: ${MDSPanelWidths.headerBreakpoint}px) {
    padding-top: 0px;
    padding-left: 12px;
  }
`;

const FiltersContainer = styled(MDSFadeHorizontalScroll)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing["2"],
}));
